import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState, useEffect } from 'react';
import { ProtectedRoute } from '../../../components/ProtectedRoute';
import { useAuth } from '../../../hooks/useAuth';
import { listProjects, createProject, archiveProject, type Project } from '../../../lib/tasks';

export const Route = createFileRoute('/kinerja/tasks/')({
  component: TasksPage,
});

function TasksPage() {
  return (
    <ProtectedRoute minimumRole="employee">
      <TasksContent />
    </ProtectedRoute>
  );
}

function TasksContent() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const canManage = user?.role && ['admin', 'hr', 'manager', 'super_admin'].includes(user.role);

  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showNew, setShowNew] = useState(false);
  const [newName, setNewName] = useState('');
  const [newDesc, setNewDesc] = useState('');
  const [creating, setCreating] = useState(false);

  const fetchProjects = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await listProjects() ;
      setProjects(data);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load projects');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchProjects(); }, []);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreating(true);
    try {
      await createProject({ name: newName, description: newDesc || undefined });
      setNewName('');
      setNewDesc('');
      setShowNew(false);
      fetchProjects();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to create project');
    } finally {
      setCreating(false);
    }
  };

  const handleArchive = async (projectId: string) => {
    if (!confirm('Archive this project?')) return;
    try {
      await archiveProject(projectId);
      fetchProjects();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to archive project');
    }
  };

  return (
    <div className="p-6 max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Tasks</h1>
          <p className="text-sm text-gray-500 mt-1">Track sprints and work items. Completed tasks auto-update your Kinerja metrics.</p>
        </div>
        {canManage && (
          <button
            onClick={() => setShowNew(true)}
            className="px-4 py-2 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700 transition-colors"
          >
            + New Project
          </button>
        )}
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">{error}</div>
      )}

      {/* New project form */}
      {showNew && (
        <form
          onSubmit={handleCreate}
          className="mb-6 bg-white rounded-xl border border-teal-200 p-5 space-y-3"
        >
          <h3 className="font-medium text-gray-900">New Project</h3>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Project Name</label>
            <input
              type="text"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="e.g. Q2 Product Sprint"
              required
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Description (optional)</label>
            <input
              type="text"
              value={newDesc}
              onChange={(e) => setNewDesc(e.target.value)}
              placeholder="Brief description"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
            />
          </div>
          <div className="flex gap-2 justify-end">
            <button
              type="button"
              onClick={() => setShowNew(false)}
              className="px-3 py-1.5 border border-gray-300 text-gray-600 rounded-lg text-sm"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={creating}
              className="px-3 py-1.5 bg-teal-600 text-white rounded-lg text-sm font-medium disabled:opacity-50"
            >
              {creating ? 'Creating...' : 'Create'}
            </button>
          </div>
        </form>
      )}

      {/* Projects grid */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-teal-500 border-t-transparent" />
        </div>
      ) : projects.length === 0 ? (
        <div className="text-center py-20 text-gray-500">
          <div className="text-5xl mb-3">🗂️</div>
          <p className="font-medium">No projects yet</p>
          {canManage && <p className="text-sm mt-1">Create your first project to start tracking work.</p>}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {projects.map((project) => (
            <div
              key={project.id}
              className="bg-white rounded-xl border border-gray-200 p-5 hover:border-teal-300 hover:shadow-sm transition-all cursor-pointer group"
              onClick={() => navigate({ to: '/kinerja/tasks/projects/$projectId/', params: { projectId: project.id } })}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-gray-900 truncate">{project.name}</h3>
                  {project.description && (
                    <p className="text-sm text-gray-500 mt-1 line-clamp-2">{project.description}</p>
                  )}
                </div>
                {canManage && (
                  <button
                    onClick={(e) => { e.stopPropagation(); handleArchive(project.id); }}
                    className="ml-2 text-gray-300 hover:text-red-400 transition-colors opacity-0 group-hover:opacity-100"
                    title="Archive"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8l1 12a2 2 0 002 2h8a2 2 0 002-2l1-12M10 12v4m4-4v4" />
                    </svg>
                  </button>
                )}
              </div>

              <div className="mt-4 flex items-center gap-3 text-xs text-gray-500">
                {project.activeSprint ? (
                  <span className="flex items-center gap-1 text-green-700 bg-green-50 px-2 py-0.5 rounded-full font-medium">
                    <span className="w-1.5 h-1.5 rounded-full bg-green-500 inline-block" />
                    {project.activeSprint.name}
                  </span>
                ) : (
                  <span className="text-gray-400">No active sprint</span>
                )}
                <span className="ml-auto">{project.openTaskCount ?? 0} open</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
